import { Capacitor } from "@capacitor/core";
import type { Session, SupabaseClient, UserIdentity } from "@supabase/supabase-js";
import { logAuth } from "../auth/debug-log";

// Calendar (contexte rencontre) + Documents readonly (import d'une
// transcription par lien de Google Doc — lecture par ID, pas d'accès Drive).
const GOOGLE_SCOPES =
  "https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/documents.readonly";

/**
 * Identité Google liée au user courant (si elle existe).
 */
export function getGoogleIdentity(session: Session | null): UserIdentity | null {
  if (!session) return null;
  return session.user?.identities?.find((id) => id.provider === "google") ?? null;
}

/**
 * Identité Google liée (peu importe la fraîcheur du provider_token).
 */
export function isGoogleCalendarLinked(session: Session | null): boolean {
  return getGoogleIdentity(session) !== null;
}

/**
 * Calendar utilisable maintenant : identité liée ET provider_token actif.
 * Si linked mais pas usable, il faut un relink pour récupérer le token (le
 * signOut Supabase efface provider_token mais pas l'identité).
 */
export function hasGoogleCalendarAccess(session: Session | null): boolean {
  return isGoogleCalendarLinked(session) && Boolean(session?.provider_token);
}

/**
 * Lance le flow OAuth Google pour lier l'identité au compte existant.
 * Le redirect retombe dans l'app via le scheme `capturebesoins://` (deep link).
 */
export async function connectGoogleCalendar(client: SupabaseClient): Promise<{
  ok: boolean;
  error?: string;
}> {
  const redirectTo = Capacitor.isNativePlatform()
    ? "capturebesoins://auth/callback"
    : `${window.location.origin}/auth/callback`;
  logAuth("info", "connectGoogleCalendar → linkIdentity", { redirectTo });
  const { error } = await client.auth.linkIdentity({
    provider: "google",
    options: {
      scopes: GOOGLE_SCOPES,
      redirectTo,
    },
  });
  if (error) {
    logAuth("warn", "linkIdentity google error", error.message);
    return { ok: false, error: error.message };
  }
  logAuth("info", "linkIdentity google ok");
  return { ok: true };
}

export function googleAccessToken(session: Session | null): string | null {
  return session?.provider_token ?? null;
}

/**
 * Si l'identité Google est déjà liée mais qu'on n'a pas de provider_token
 * (cas après signOut/signIn), on unlink puis relink pour récupérer un token
 * frais via le flow OAuth. L'utilisateur passe par Google mais ne devrait pas
 * voir de prompt (autorisations déjà accordées).
 */
export async function reconnectGoogleCalendar(
  client: SupabaseClient,
  session: Session,
): Promise<{ ok: boolean; error?: string }> {
  const identity = getGoogleIdentity(session);
  if (!identity) {
    logAuth("warn", "reconnectGoogleCalendar : aucune identité Google liée");
    return connectGoogleCalendar(client);
  }
  logAuth("info", "reconnectGoogleCalendar → unlinkIdentity", { identityId: identity.identity_id });
  const { error: unlinkErr } = await client.auth.unlinkIdentity(identity);
  if (unlinkErr) {
    logAuth("warn", "unlinkIdentity error", unlinkErr.message);
    return { ok: false, error: unlinkErr.message };
  }
  return connectGoogleCalendar(client);
}
